import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { tubularTypeColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import TemporaryRestrictedPipeMovementTable from './TemporaryRestrictedPipeMovementTable'
import TemporaryRestrictedConnectionsTable from './TemporaryRestrictedConnectionsTable'
import TemporaryRestrictedRopsTable from './TemporaryRestrictedRopsTable'
import { fetchEntityOptions } from '../../../widgets/static-data/api/api'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptyTubularType } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { CustomExpandRowIcon } from './components/CustomExpandRowIcon'
import { saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { Key, RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const TemporaryTubularTypeTable = memo(
    ({
        parent,
        data,
        setData,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: {
        parent: RunDto
        data: TemporaryDataType
        setData: TemporarySetDataType
        localErrors: LocalErrorsTypes[]
        removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    }): ReactElement => {
        const [optionsOfTubularTypeEntity, setOptionsOfTubularTypeEntity] = useState(null)
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity,
            expandedRowKeys,
            setExpandedRowKeysByEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()

        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const tubularsType = useMemo(
            () =>
                data.tubulars
                    .filter(tub => tub.parent == parent.nodeId)
                    .map(tubular => ({
                        ...tubular,
                        key: String(tubular.rowId),
                        permanentTubularInstallation: tubular?.permanentTubularInstallation ?? false,
                        parent: parent.nodeId
                    })),
            [data.tubulars, parent.nodeId]
        )

        useEffect(() => {
            fetchEntityOptions('tubulartype').then(data => {
                const hideUnrestrictedValue = {
                    ...data,
                    restrictedConnectionType: data.restrictedConnectionType.filter(e => e.acronym !== 'UN')
                }
                setOptionsOfTubularTypeEntity(hideUnrestrictedValue)
            })

            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const tubularType of data.tubulars) {
                newStartDate[tubularType.rowId] = tubularType.start ? new Date(tubularType.start) : ''
                newEndDate[tubularType.rowId] = tubularType.end ? new Date(tubularType.end) : ''
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        // Functions
        const handleEditData = useCallback(
            (entity: EntityDto[], row: EntityDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => String(row.rowId) === String(item.rowId))
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'TUBULAR_TYPE').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setTubulars(newData as TubularTypeDto[])
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`tubulars=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: EntityDto): void => {
                handleEditData(data.tubulars, row, setData)
            },
            [data.tubulars, handleEditData, setData]
        )

        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.tubulars]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.start = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.tubulars, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.tubulars]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.end = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.tubulars, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.tubulars]

            const newEntity: TubularTypeDto = {
                ...emptyTubularType,
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'TUBULAR_TYPE').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setTubulars(oldEntity)
                goToNewRow()
            })
        }, [data.tubulars, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: TubularTypeDto): void => {
                const newData = data.tubulars.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setTubulars(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [checkedRows, data.tubulars, formatCheckedRows, removeErrorWhenCheckedRowIsRemoved, setCheckedRows, setData]
        )

        const handleBooleanCell = useCallback(
            (record: TubularTypeDto, key: string): void => {
                const newData = [...data.tubulars]
                const currentRow = newData.find(row => String(row.rowId) === String(record.rowId))
                currentRow[key] = !record[key]
                handleEditSave(currentRow)
            },
            [data.tubulars, handleEditSave]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: TubularTypeDto): ReactElement => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )

        const handleCalendarElementEndDate = useCallback(
            (record: TubularTypeDto): ReactElement => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const handleRadioButtonElement = useCallback(
            (record: TubularTypeDto): ReactElement => {
                return (
                    <input
                        checked={record.permanentTubularInstallation}
                        onChange={() => handleBooleanCell(record, 'permanentTubularInstallation')}
                        type="checkbox"
                    />
                )
            },
            [handleBooleanCell]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const tubularCols = useMemo(
            () =>
                tubularTypeColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfTubularTypeEntity,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    handleRadioButtonElement,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.runs,
                    localErrors
                ),
            [
                data.runs,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                handleRadioButtonElement,
                localErrors,
                moveRowToAnotherParent,
                optionsOfTubularTypeEntity
            ]
        )

        const expandedRowRender = useCallback(
            (record: TubularTypeDto): ReactElement => {
                return (
                    <>
                        <TemporaryRestrictedPipeMovementTable
                            parent={record as TubularTypeDto}
                            data={data}
                            setData={setData}
                            optionsOfTubularTypeEntity={optionsOfTubularTypeEntity}
                            localErrors={localErrors}
                            removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                        />
                        <TemporaryRestrictedConnectionsTable
                            parent={record as TubularTypeDto}
                            data={data}
                            setData={setData}
                            optionsOfTubularTypeEntity={optionsOfTubularTypeEntity}
                            localErrors={localErrors}
                            removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                        />
                        <TemporaryRestrictedRopsTable
                            parent={record as TubularTypeDto}
                            data={data}
                            setData={setData}
                            optionsOfTubularTypeEntity={optionsOfTubularTypeEntity}
                            localErrors={localErrors}
                            removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                        />
                    </>
                )
            },
            [data, localErrors, optionsOfTubularTypeEntity, removeErrorWhenCheckedRowIsRemoved, setData]
        )

        const expandIcon = useCallback(({ expanded, onExpand, record }): ReactElement => {
            return <CustomExpandRowIcon expanded={expanded} onExpand={onExpand} record={record as TubularTypeDto} />
        }, [])

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="tubulars type" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'tubulars', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: TubularTypeDto[]) => {
                    selectAllRows({
                        data,
                        type: 'tubulars',
                        parentNodeId: parent.nodeId,
                        selectedRows
                    })
                },
                onSelect: (
                    record: EntityDto,
                    selected: boolean,
                    selectedRows: TubularTypeDto[],
                    nativeEvent: Event
                ) => {
                    selectAllRows({
                        data,
                        type: 'tubulars',
                        parentNodeId: parent.nodeId,
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const handleExpandedRowsChange = useCallback(
            (keys: React.Key[]): void => {
                setExpandedRowKeysByEntity('tubulars', keys as Key[])
            },
            [setExpandedRowKeysByEntity]
        )

        const expandable = useMemo(
            () => ({
                expandedRowRender,
                expandIcon,
                expandedRowKeys: expandedRowKeys?.tubulars,
                onExpandedRowsChange: handleExpandedRowsChange,
                defaultExpandAllRows: false
            }),
            [expandIcon, expandedRowKeys?.tubulars, expandedRowRender, handleExpandedRowsChange]
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={tubularsType}
                    columns={tubularCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryTubularTypeTable.displayName = 'TemporaryTubularTypeTable'

export default TemporaryTubularTypeTable

const TableContainer = styled.main``
